import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useUnlistCar } from '@/api/partners/cars/use-unlist-car';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Text } from '@/ui';

const unlistCarItems = [
  'The car is hidden and cannot receive new reservations.',
  'Existing reservations remain active.',
  'Calendar, pricing, and rules stay saved.',
  'You can relist at any time.',
];

interface UnlistCarModalProps {
  visible?: boolean;
  car: Car;
  onClose: () => void;
  onSuccess: () => void;
  style?: any;
}

export function UnlistCarModal({
  visible = false,
  car,
  onClose,
  onSuccess,
  style,
}: UnlistCarModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutateAsync: unlistCar, isPending: isUnlistingCar } = useUnlistCar();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleUnlistCar = async () => {
    try {
      await unlistCar({
        id: car.id,
      });

      mixpanelService.track('Car Unlisted', {
        'Car VIN': car.vin,
      });

      onSuccess();
    } catch (e: any) {
      setErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>
        <View
          style={{
            flexDirection: 'row',
            paddingTop: 16,
            paddingLeft: 16,
          }}
        >
          <View>
            <Text type="title2">Unlist Car</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <View style={{ padding: 16, gap: 24 }}>
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            Unlisting pauses your listing immediately.
          </Text>

          <View style={{ gap: 8 }}>
            {unlistCarItems.map((item) => {
              return (
                <View
                  key={item}
                  style={{
                    flexDirection: 'row',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <View
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: 8,
                      backgroundColor: theme.colors.icon.color,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {item}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        <View
          style={{
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: 16,
            paddingTop: 0,
            gap: 24,
          }}
        >
          <View style={styles.buttonsContainer}>
            <Button
              text="Go Back"
              type="ghost"
              onPress={() => {
                setShowModal(false);
                onClose();
              }}
              style={styles.button}
            />

            <Button
              text="Unlist Car"
              type="primary"
              onPress={handleUnlistCar}
              style={[styles.button]}
            />
          </View>

          {errorMessage ? (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Error: {errorMessage}
            </Text>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 48,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  buttonsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    width: '100%',
    gap: 8,
    justifyContent: {
      xs: 'center',
      md: 'flex-end',
    },
    paddingLeft: 16,
    paddingRight: 16,
  },
}));
